import React from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import { fmtEUR } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import {
  CalendarClock, Euro, TrendingUp, TrendingDown, Package, Phone, Mail, Globe, User,
  ShoppingCart, Minus,
} from "lucide-react";

const fmtD = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
};

const klantSinds = (dagen) => {
  if (dagen == null) return "—";
  const jaren = Math.floor(dagen / 365);
  const maanden = Math.floor((dagen % 365) / 30);
  if (jaren > 0) return `${jaren} jaar${maanden ? ` ${maanden} mnd` : ""}`;
  if (maanden > 0) return `${maanden} maand${maanden > 1 ? "en" : ""}`;
  return `${dagen} dagen`;
};

function Kpi({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid="klant-kpi">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="text-2xl font-display font-medium mt-2 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function TrendBadge({ pct }) {
  if (pct == null) return <Badge variant="outline" className="gap-1 text-muted-foreground"><Minus className="h-3 w-3" /> n.v.t.</Badge>;
  const up = pct >= 0;
  return (
    <Badge variant="outline" className={`gap-1 ${up ? "text-emerald-600 border-emerald-300" : "text-red-600 border-red-300"}`}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{up ? "+" : ""}{pct}%
    </Badge>
  );
}

export function KlantDashboard({ data, org, contacts = [] }) {
  if (!data) return <div className="text-muted-foreground text-sm py-8 text-center">Laden…</div>;
  const jaar = data.jaar;
  const groei = data.omzet_vorig_jaar > 0
    ? Math.round((data.omzet_dit_jaar - data.omzet_vorig_jaar) / data.omzet_vorig_jaar * 100)
    : null;
  const contact = contacts[0];

  return (
    <div className="space-y-6" data-testid="klant-dashboard">
      {data.aantal_facturen === 0 && (
        <div className="rounded-lg bg-secondary/50 border border-border px-4 py-3 text-sm text-muted-foreground" data-testid="klant-geen-facturen">
          Nog geen facturen voor deze klant. Zodra er gefactureerd wordt, verschijnen hier omzet, trends en bestelgegevens.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={CalendarClock} label="Klant sinds" value={klantSinds(data.klant_sinds_dagen)}
          sub={data.eerste_factuurdatum ? `1e factuur: ${fmtD(data.eerste_factuurdatum)}` : "Geen facturen"} />
        <Kpi icon={Euro} label="Omzet totaal" value={fmtEUR(data.omzet_totaal)}
          sub={`${data.aantal_facturen} facturen`} />
        <Kpi icon={TrendingUp} label={`Omzet ${jaar} (YTD)`} value={fmtEUR(data.omzet_dit_jaar)}
          sub={groei == null ? `${jaar - 1} YTD: ${fmtEUR(data.omzet_vorig_jaar)}` : `${groei >= 0 ? "+" : ""}${groei}% t.o.v. ${jaar - 1} (YTD)`} />
        <Kpi icon={ShoppingCart} label="Laatste bestelling" value={fmtD(data.laatste_besteldatum)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Grafiek omzet dit jaar vs vorig jaar */}
        <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5">
          <div className="text-sm font-medium mb-4">Omzet per maand — {jaar} vs {jaar - 1}</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.omzet_per_maand} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border" />
              <XAxis dataKey="maand" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={48} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtEUR(v)} />
              <Legend />
              <Bar name={`${jaar - 1}`} dataKey="vorig_jaar" fill="#cbd5e1" radius={[3, 3, 0, 0]} />
              <Bar name={`${jaar}`} dataKey="dit_jaar" fill="#16a34a" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Klantinfo */}
        <div className="bg-card border border-border rounded-xl p-5 space-y-4" data-testid="klant-info">
          <div className="text-sm font-medium">Klantinfo</div>
          <div className="space-y-2 text-sm">
            {org?.telefoon && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-muted-foreground" />{org.telefoon}</div>}
            {org?.email && <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-muted-foreground" />{org.email}</div>}
            {org?.website && <div className="flex items-center gap-2"><Globe className="h-3.5 w-3.5 text-muted-foreground" />{org.website}</div>}
            {!org?.telefoon && !org?.email && !org?.website && <div className="text-muted-foreground">Geen contactgegevens.</div>}
          </div>
          <div className="pt-3 border-t border-border">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Contactpersoon</div>
            {contact ? (
              <div>
                <div className="font-medium flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  {(contact.voornaam || contact.achternaam) ? `${contact.voornaam} ${contact.achternaam}`.trim() : (contact.functie || "Contactpersoon")}
                </div>
                <div className="text-sm text-muted-foreground ml-6">{[contact.functie, contact.rol].filter(Boolean).join(" · ")}</div>
                {contact.email && <div className="text-sm text-muted-foreground ml-6">{contact.email}</div>}
                {(contact.telefoon || contact.mobiel) && <div className="text-sm text-muted-foreground ml-6">{contact.telefoon || contact.mobiel}</div>}
                {contacts.length > 1 && <div className="text-xs text-muted-foreground ml-6 mt-1">+{contacts.length - 1} meer onder tab Contactpersonen</div>}
              </div>
            ) : <div className="text-sm text-muted-foreground">Geen contactpersoon.</div>}
          </div>
        </div>
      </div>

      {/* Top 3 producten */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="text-sm font-medium mb-4">Top 3 gefactureerde producten</div>
        {data.top_producten.length === 0 ? (
          <div className="text-sm text-muted-foreground">Nog geen gefactureerde producten.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {data.top_producten.map((p, i) => (
              <div key={i} className="rounded-lg border border-border p-4" data-testid={`top-product-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 font-medium truncate">
                    <Package className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="truncate">{p.product}</span>
                  </div>
                  <TrendBadge pct={p.trend_pct} />
                </div>
                {p.code && <div className="text-xs text-muted-foreground mt-1">{p.code}</div>}
                <div className="mt-3 text-xl font-display font-medium tabular-nums">{fmtEUR(p.omzet)}</div>
                <div className="text-xs text-muted-foreground mt-1">{p.aantal} stuks · laatst: {fmtD(p.laatste_besteldatum)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
